import * as Cesium from 'cesium';
import { APP_CONFIG } from '../config/appConfig';
import { DATA_SOURCES } from '../config/dataSources';

export interface TerrainLod3Coverage {
  rectangle: Cesium.Rectangle;
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface TerrainLod3LoadResult {
  tileset: Cesium.Cesium3DTileset;
  coverage: TerrainLod3Coverage;
  setVisible: (visible: boolean) => void;
}

function coverageFromTileset(
  tileset: Cesium.Cesium3DTileset,
): TerrainLod3Coverage {
  const rectangle = Cesium.Rectangle.fromBoundingSphere(tileset.boundingSphere);
  return {
    rectangle,
    west: Cesium.Math.toDegrees(rectangle.west),
    south: Cesium.Math.toDegrees(rectangle.south),
    east: Cesium.Math.toDegrees(rectangle.east),
    north: Cesium.Math.toDegrees(rectangle.north),
  };
}

function coveragePolygon(coverage: TerrainLod3Coverage): Cesium.ClippingPolygon {
  const { west, south, east, north } = coverage;
  return new Cesium.ClippingPolygon({
    positions: Cesium.Cartesian3.fromDegreesArray([
      west,
      south,
      east,
      south,
      east,
      north,
      west,
      north,
    ]),
  });
}

export async function loadTerrainLod3(
  viewer: Cesium.Viewer,
  onError: (error: unknown) => void,
): Promise<TerrainLod3LoadResult> {
  if (!DATA_SOURCES.terrainLod3) {
    throw new Error('検証済みLOD3 Terrain URLが未登録です。');
  }

  try {
    const tileset = await Cesium.Cesium3DTileset.fromUrl(
      DATA_SOURCES.terrainLod3,
      {
        maximumScreenSpaceError:
          APP_CONFIG.performance.terrainLod3MaximumScreenSpaceError,
        cacheBytes: APP_CONFIG.performance.terrainLod3CacheBytes,
        maximumCacheOverflowBytes: 64 * 1024 * 1024,
        dynamicScreenSpaceError: true,
        scene: viewer.scene,
        show: false,
      },
    );
    viewer.scene.primitives.add(tileset);

    const coverage = coverageFromTileset(tileset);
    // 小範囲メッシュの境界は不完全なため、背景地形を常に残す。
    // boundingSphereから求めた矩形で切り抜くとメッシュ外にも穴が開く。
    if (viewer.scene.globe.clippingPolygons) {
      viewer.scene.globe.clippingPolygons.enabled = false;
    }

    const setVisible = (visible: boolean): void => {
      tileset.show = visible;
      viewer.scene.requestRender();
    };
    setVisible(true);
    return { tileset, coverage, setVisible };
  } catch (error) {
    onError(error);
    throw error;
  }
}
